import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import PutAwayList from '../components/PutAwayList';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';
import ModalCreacionProductoRapida from '../components/ModalCreacionProductoRapida';
import ProveedorForm from '../components/ProveedorForm';
import { useGlobal } from '../context/GlobalContext';
import { PaginationInfo } from '../types';
import '../styles/RecepcionMercancia.css';

// Interfaces principales
interface Proveedor {
    id: number;
    nombre: string;
}

interface FacturaItem {
    id?: number;
    producto_id: number;
    nombre: string;
    cantidad_facturada?: number | null;
    cantidad_fisica: number;
    precioUnitario: number;
    total: number;
    alertaPrecio?: boolean;
    saved?: boolean;
}

const RecepcionMercancia: React.FC = () => {
    const { data, reLoad } = useGlobal();
    const proveedores = data?.proveedores || [];
    const [proveedorSeleccionado, setProveedorSeleccionado] = useState<number | ''>('');
    const [numeroFactura, setNumeroFactura] = useState('');
    const [facturaActivaId, setFacturaActivaId] = useState<number | null>(null);
    const [items, setItems] = useState<FacturaItem[]>([]);
    const [facturaProcesadaItems, setFacturaProcesadaItems] = useState<FacturaItem[] | null>(null);
    const [barcodeMode, setBarcodeMode] = useState(true);

    // Borradores pendientes
    const [facturasBorrador, setFacturasBorrador] = useState<any[]>([]);
    const [facturaBorradorSeleccionada, setFacturaBorradorSeleccionada] = useState<number | ''>('');

    // Búsqueda Manual con debounce + paginación
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searchPage, setSearchPage] = useState(1);
    const [searchPagination, setSearchPagination] = useState<Partial<PaginationInfo>>({});

    // Modal Creacion Rapida
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showProveedorModal, setShowProveedorModal] = useState(false);
    const [scannedBarcodeForModal, setScannedBarcodeForModal] = useState('');
    const subcategorias = data?.subcategorias || [];
    const ubicaciones = data?.ubicaciones || [];

    // Lógica para Barcode (Skill)
    // El escáner emula teclado. Capturamos en un buffer.
    const barcodeBuffer = useRef('');
    const timeoutRef = useRef<any>(null);

    const agregarItemLocal = (producto: any) => {
        const precioUnitario = producto.precio_compra || 0;
        const stockTeorico = producto.Inventario?.stock || producto.cantidad_disponible || producto.stock || producto.cantidad || '';
        const nuevoItem: FacturaItem = {
            producto_id: producto.id,
            nombre: producto.nombre,
            cantidad_facturada: stockTeorico ? Number(stockTeorico) : null,
            cantidad_fisica: stockTeorico ? Number(stockTeorico) : 1,
            precioUnitario: precioUnitario,
            total: precioUnitario * (stockTeorico ? Number(stockTeorico) : 1),
            alertaPrecio: false
        };

        setItems(prev => [...prev, nuevoItem]);
        setSearchResults([]);
        setSearchQuery('');
        setSearchPage(1);
        setSearchPagination({});
    };

    const buscarProductoPorCodigo = async (codigo: string) => {
        try {
            const res = await api.get(`/productos/buscar?query=${encodeURIComponent(codigo)}&page=1&limit=10&light=true`);
            const productos = res.data.data || [];
            if (productos.length > 0) {
                agregarItemLocal(productos[0]);
            } else {
                setScannedBarcodeForModal(codigo);
                setIsModalOpen(true);
            }
        } catch (error) {
            console.error('Error al buscar por código', error);
        }
    };

    const buscarProductos = useCallback(async (query: string, page: number) => {
        if (!query.trim()) {
            setSearchResults([]);
            setSearchPagination({});
            return;
        }
        try {
            const res = await api.get(`/productos/buscar?query=${encodeURIComponent(query)}&page=${page}&limit=10&light=true`);
            setSearchResults(res.data.data || []);
            setSearchPagination(res.data.pagination || {});
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        setSearchPage(1);
        buscarProductos(query, 1);
    };

    const handleSearchPageChange = (newPage: number) => {
        setSearchPage(newPage);
        buscarProductos(searchQuery, newPage);
    };

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (!barcodeMode || !facturaActivaId || isModalOpen || !e || !e.key) return;

        // Limpiar timeout anterior
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        // Si es Enter, se procesa el código
        if (e.key === 'Enter' && barcodeBuffer.current.length > 0) {
            buscarProductoPorCodigo(barcodeBuffer.current);
            barcodeBuffer.current = '';
            e.preventDefault();
            return;
        }

        // Ignorar teclas que no sean letras o números básicos (depende del tipo de código de barras)
        if (e.key.length === 1) {
            barcodeBuffer.current += e.key;
        }

        // Si pasaron más de 100ms desde el último tecleo, resetear el buffer
        // Esto asume que el escáner escribe muy rápido vs humano
        timeoutRef.current = setTimeout(() => {
            barcodeBuffer.current = '';
        }, 100);
    }, [barcodeMode, facturaActivaId, isModalOpen]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const cargarBorradores = useCallback(async () => {
        try {
            const res = await api.get('/facturas?limit=100');
            const facturasData = res?.data?.facturas;
            const facturas = Array.isArray(facturasData) ? facturasData : [];
            const borradores = facturas.filter((f: any) => f.estado === 'Borrador');
            setFacturasBorrador(borradores);
        } catch (e) {
            console.error('Error cargando borradores', e);
        }
    }, []);

    useEffect(() => {
        cargarBorradores();
    }, [cargarBorradores]);

    const iniciarFactura = async () => {
        try {
            if (!proveedorSeleccionado || !numeroFactura) return alert('Datos vacíos');
            const res = await api.post('/facturas', {
                proveedor_id: Number(proveedorSeleccionado),
                numero_factura: numeroFactura
            });
            setFacturaActivaId(res.data.id);
            alert('Factura iniciada. Ahora puede escanear productos.');
        } catch (error: any) {
            alert(error.response?.data?.error || 'Error al iniciar factura');
        }
    };

    const cargarFacturaBorrador = async () => {
        if (!facturaBorradorSeleccionada) return;
        try {
            const res = await api.get(`/facturas/${facturaBorradorSeleccionada}`);
            const factura = res.data;
            setProveedorSeleccionado(factura.proveedor_id);
            setNumeroFactura(factura.numero_factura);
            setFacturaActivaId(factura.id);

            // Map items. Sequelize uses plural 'HistorialCompras' by default for 1:N relations
            const ht = factura.HistorialCompras || factura.HistorialCompra || [];
            const parsedItems = ht.map((hc: any) => ({
                id: hc.id,
                producto_id: hc.producto_id,
                nombre: hc.Producto?.nombre || 'Producto Desconocido',
                cantidad_facturada: hc.cantidad_facturada,
                cantidad_fisica: hc.cantidad_fisica,
                precioUnitario: hc.precioUnitario,
                total: hc.total,
                alertaPrecio: false,
                saved: true
            }));
            setItems(parsedItems);
        } catch (error) {
            alert('Error al cargar la factura en borrador');
        }
    };

    const procesarFactura = async () => {
        if (!facturaActivaId) return;
        try {
            await api.put(`/facturas/${facturaActivaId}/procesar`, { items });
            alert('Factura Procesada! El Kardex y el Inventario han sido verificados y actualizados.');
            setFacturaProcesadaItems([...items]);
            setFacturaActivaId(null);
            setItems([]);
            setNumeroFactura('');
        } catch (error: any) {
            alert(error.response?.data?.error || 'Error al procesar factura');
        }
    };



    const handleProductCreated = (id: number, productoData: any) => {
        agregarItemLocal(productoData);
        setIsModalOpen(false);
        setScannedBarcodeForModal('');
    };

    const handleProveedorCreated = (message: string, proveedor?: Proveedor) => {
        setShowProveedorModal(false);
        reLoad();
        if (proveedor?.id) {
            setProveedorSeleccionado(proveedor.id);
        }
    };

    const syncItemToBackend = async (index: number) => {
        const item = items[index];
        if (!item || !facturaActivaId) return;
        try {
            const res = await api.post(`/facturas/${facturaActivaId}/items`, {
                producto_id: item.producto_id,
                cantidad_fisica: item.cantidad_fisica,
                cantidad_facturada: item.cantidad_facturada,
                precioUnitario: item.precioUnitario
            });
            const newItems = [...items];
            if (newItems[index]) {
                newItems[index].saved = true;
                newItems[index].id = res.data.id;
                setItems(newItems);
            }
            alert('Guardado parcial OK');
        } catch (e) {
            alert('Error al sincronizar item');
        }
    };

    const removeItem = async (index: number) => {
        const item = items[index];
        if (!item) return;

        if (!item.saved) {
            setItems(prev => prev.filter((_, i) => i !== index));
            return;
        }

        try {
            await api.delete(`/facturas/${facturaActivaId}/items/${item.id}`);
            setItems(prev => prev.filter((_, i) => i !== index));
        } catch (e) {
            alert('No se pudo eliminar el ítem del servidor.');
        }
    };

    const handleItemChange = (index: number, field: keyof FacturaItem, value: any) => {
        const newItems = [...items];
        if (!newItems[index]) return;
        (newItems[index] as any)[field] = value;

        // Reset saved flag because data changed
        newItems[index].saved = false;

        // Recalcular total
        if (field === 'cantidad_fisica' || field === 'precioUnitario') {
            newItems[index].total = newItems[index].cantidad_fisica * newItems[index].precioUnitario;
        }

        setItems(newItems);
    };

    const isPriceAlert = (item: FacturaItem) => item.precioUnitario === 0;

    return (
        <div className="recepcion-container">
            <h2 className="recepcion-title">Recepción de Mercancía / Facturas</h2>

            {/* INICIO DE FACTURA */}
            {!facturaActivaId ? (
                <>
                    {facturasBorrador.length > 0 && (
                        <div className="recepcion-form-header" style={{ marginBottom: '1rem', backgroundColor: '#fffbeb', borderColor: '#fde68a' }}>
                            <div>
                                <label className="recepcion-label">Continuar Borrador Pendiente</label>
                                <select
                                    className="recepcion-select"
                                    value={facturaBorradorSeleccionada}
                                    onChange={(e) => setFacturaBorradorSeleccionada(Number(e.target.value))}
                                >
                                    <option value="">-- Seleccionar Factura --</option>
                                    {facturasBorrador.map((f: any) => (
                                        <option key={f.id} value={f.id}>
                                            Factura: {f.numero_factura} ({f.Proveedor?.nombre})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="recepcion-label">&nbsp;</label>
                                <button
                                    className="recepcion-btn-secondary"
                                    onClick={cargarFacturaBorrador}
                                    style={{ width: '100%' }}
                                >
                                    Cargar Borrador
                                </button>
                            </div>
                        </div>
                    )}

                    {facturasBorrador.length > 0 && <div style={{ textAlign: 'center', marginBottom: '1rem', fontWeight: 'bold' }}>O iniciar una nueva:</div>}

                    <div className="recepcion-form-header">
                        <div>
                            <label className="recepcion-label">Proveedor</label>
                            <select
                                className="recepcion-select"
                                value={proveedorSeleccionado}
                                onChange={(e) => setProveedorSeleccionado(Number(e.target.value))}
                            >
                                <option value="">Seleccione...</option>
                                {proveedores.map((p: Proveedor) => (
                                    <option key={p.id} value={p.id}>{p.nombre}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="recepcion-btn-secondary"
                                onClick={() => setShowProveedorModal(true)}
                                style={{ marginTop: '6px', width: '100%', fontSize: '0.85rem' }}
                            >
                                + Crear Proveedor Nuevo
                            </button>
                        </div>
                        <div>
                            <label className="recepcion-label">Número de Factura</label>
                            <input
                                type="text"
                                className="recepcion-input"
                                value={numeroFactura}
                                onChange={e => setNumeroFactura(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="recepcion-label">&nbsp;</label>
                            <button
                                className="recepcion-btn-primary"
                                onClick={iniciarFactura}
                            >
                                Comenzar Recepción
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <div className="recepcion-active-status">
                    <p><strong>Factura en progreso:</strong> {numeroFactura}</p>
                    <button onClick={procesarFactura} className="recepcion-btn-success">
                        Finalizar y Procesar al Kardex
                    </button>
                </div>
            )}

            {facturaActivaId && (
                <>
                    {/* ZONA DE ESCANEO Y BÚSQUEDA MANUAL */}
                    <div className="recepcion-toolbar">
                        <div className="recepcion-toolbar-left">
                            <label style={{ display: 'flex', alignItems: 'center' }}>
                                <input type="checkbox" checked={barcodeMode} onChange={() => setBarcodeMode(!barcodeMode)} />
                                <span style={{ marginLeft: '0.5rem', fontWeight: 500 }}>Modo Escáner Activo</span>
                            </label>
                            <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                (Teclado capturado globalmente, beep sim!)
                            </div>
                        </div>

                        <div className="recepcion-toolbar-right">
                            <div className="recepcion-searchbar-wrap">
                                <SearchBar
                                    placeholder="Buscar por nombre/ref..."
                                    onSearch={handleSearch}
                                />
                            </div>
                            <button className="recepcion-btn-accent" onClick={() => { setScannedBarcodeForModal(''); setIsModalOpen(true); }}>+ Nuevo Producto</button>
                        </div>
                    </div>

                    {/* RESULTADOS DE BUSQUEDA CON DEBOUNCE Y PAGINACIÓN */}
                    {searchResults.length > 0 && (
                        <div className="recepcion-search-results">
                            <h4 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>
                                Seleccionar Producto:
                                {searchPagination.totalItems !== undefined && ` (${searchPagination.totalItems} encontrados)`}
                            </h4>
                            {searchResults.map(p => (
                                <button
                                    key={p.id}
                                    className="recepcion-search-result-item"
                                    onClick={() => agregarItemLocal(p)}
                                >
                                    <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                                    <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                                        {p.descripcion ? p.descripcion + ' | ' : ''}Marca: {p.marca || 'N/A'} | Modelo: {p.modelo || 'N/A'} | Ref: {p.referencia || 'N/A'}
                                    </div>
                                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#059669', marginTop: '0.25rem' }}>
                                        Último Precio Compra: ${p.precio_compra || 0}
                                    </div>
                                </button>
                            ))}
                            {searchPagination.totalPages && searchPagination.totalPages > 1 && (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <Pagination
                                        currentPage={searchPagination.currentPage || 1}
                                        totalPages={searchPagination.totalPages || 1}
                                        hasNextPage={searchPage < (searchPagination.totalPages || 1)}
                                        hasPrevPage={searchPage > 1}
                                        onPageChange={handleSearchPageChange}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* TABLA DE DETALLE (CONTEO CIEGO) */}
                    <table className="recepcion-table">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th className="col-factura" title="Visible solo en oficina, no por el bodeguero en modo ciego">Q. Factura (Teoría)</th>
                                <th className="col-fisica" title="Ingresado por quien recibe físicamente">Q. Física (Ciego)</th>
                                <th>Precio U. (Sin IVA)</th>
                                <th>Total</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && <tr><td colSpan={6} className="recepcion-empty-row">No hay ítems en esta factura. Escanee un producto.</td></tr>}
                            {items.map((item, idx) => (
                                <tr key={idx} className={`${item.alertaPrecio ? 'recepcion-table-row-alert' : ''} ${isPriceAlert(item) ? 'price-warning' : ''}`}>
                                    <td>{item.nombre}</td>
                                    <td>
                                        <input
                                            type="number"
                                            className="recepcion-input-small"
                                            value={item.cantidad_facturada || ''}
                                            onChange={e => handleItemChange(idx, 'cantidad_facturada', Number(e.target.value))}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="recepcion-input-small recepcion-input-highlight"
                                            value={item.cantidad_fisica || ''}
                                            onChange={e => handleItemChange(idx, 'cantidad_fisica', Number(e.target.value))}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="recepcion-input-small"
                                            style={{ width: '6rem' }}
                                            value={item.precioUnitario || ''}
                                            onChange={e => handleItemChange(idx, 'precioUnitario', Number(e.target.value))}
                                        />
                                    </td>
                                    <td style={{ fontWeight: 'bold' }}>${item.total.toFixed(2)}</td>
                                    <td style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                        {!item.saved ? (
                                            <button
                                                style={{ 
                                                    backgroundColor: '#fee2e2', 
                                                    color: '#991b1b', 
                                                    border: '1px solid #fca5a5', 
                                                    padding: '4px 8px', 
                                                    borderRadius: '4px', 
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    width: '100px'
                                                }}
                                                onClick={() => syncItemToBackend(idx)}
                                            >
                                                Guardar BD ⚠️
                                            </button>
                                        ) : (
                                            <button
                                                style={{ 
                                                    backgroundColor: '#d1fae5', 
                                                    color: '#065f46', 
                                                    border: '1px solid #6ee7b7', 
                                                    padding: '4px 8px', 
                                                    borderRadius: '4px', 
                                                    cursor: 'default',
                                                    fontWeight: 'bold',
                                                    width: '100px'
                                                }}
                                                disabled
                                            >
                                                Guardado ✅
                                            </button>
                                        )}
                                        <button
                                            className="recepcion-btn-danger"
                                            onClick={() => removeItem(idx)}
                                            title="Eliminar ítem"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            {/* VISTA DE PUT-AWAY TRAS PROCESAR */}
            {facturaProcesadaItems && (
                <div className="recepcion-putaway-section">
                    <button
                        className="recepcion-link print-hidden"
                        onClick={() => setFacturaProcesadaItems(null)}
                    >
                        Volver a Recepción
                    </button>
                    <PutAwayList items={facturaProcesadaItems as any} facturaNumero="Última Procesada" />
                </div>
            )}

            <ModalCreacionProductoRapida
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={handleProductCreated}
                initialBarcode={scannedBarcodeForModal}
                subcategorias={subcategorias}
                ubicaciones={ubicaciones}
            />

            {showProveedorModal && (
                <div className="modal">
                    <div className="modal-content">
                        <ProveedorForm
                            onClose={() => setShowProveedorModal(false)}
                            onSuccess={handleProveedorCreated}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecepcionMercancia;
